import React, { useState, useEffect } from "react";
import { apiFetch } from "../../api.js";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout.js";

const RtoCityMasterGrid = () => {
  const navigate = useNavigate();
  const [gridData, setGridData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    fetchGrid();
  }, []);

  const fetchGrid = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(
        "/api/rto-city-grid"
      );
      const data = await response.json();
      setGridData(data);
      setFilteredData(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching grid:", error);
      setLoading(false);
    }
  };

  const handleEdit = async (rtoId) => {
    try {
      setLoading(true);
      const response = await apiFetch(
        `/api/rto-city-master/edit/${rtoId}`
      );
      const data = await response.json();
      console.log("📦 RTO Edit API Response:", data);
      if (!data || Object.keys(data).length === 0) {
        alert("❌ No record found for this RTO City");
        return;
      }
      navigate("/RtoCityMaster", { state: { editData: data } });
    } catch (err) {
      console.error("❌ Error fetching RTO edit data:", err);
      alert("Failed to fetch RTO City details. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      setFilteredData(gridData);
      return;
    }
    try {
      setLoading(true);
      const response = await apiFetch(
        `/api/rto-city-search?keyword=${encodeURIComponent(searchKeyword)}`
      );
      const data = await response.json();
      setGridData(data);
      setFilteredData(data);
      setLoading(false);
    } catch (error) {
      console.error("Error during search:", error);
      setLoading(false);
    }
  };

  const handleDelete = async (rtoId) => {
    if (!window.confirm("Are you sure you want to delete this RTO City?"))
      return;
    try {
      setLoading(true);
      const response = await apiFetch(
        `/api/rto-city-master/delete/${rtoId}`,
        { method: "DELETE" },
      );
      const data = await response.json();
      if (data.success) {
        alert("✅ " + data.message);
        fetchGrid();
      } else {
        alert("❌ " + data.message);
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      alert("Error deleting record: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  /*STYLE*/
  const styles = {
    pageContainer: {
      width: "100%",
      minHeight: "100vh",
      backgroundColor: "#f4f6f8",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "10px",
      boxSizing: "border-box",
    },
    container: {
      width: "100%",
      maxWidth: "1400px",
      padding: "15px",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      backgroundColor: "#fff",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "15px",
      gap: "10px",
    },
    leftHeader: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "8px",
      flex: 1,
      minWidth: "250px",
    },
    title: { fontSize: "1.3rem", fontWeight: "bold" },
    addButton: {
      padding: "10px 16px",
      backgroundColor: "#3a28a0",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
      transition: "0.3s",
    },
    searchInput: {
      flex: "1",
      minWidth: "160px",
      padding: "8px 10px",
      borderRadius: "4px",
      border: "1px solid #ccc",
    },
    searchButton: {
      padding: "8px 14px",
      backgroundColor: "#2196F3",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
    },
    select: {
      padding: "8px 10px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      backgroundColor: "#fff",
      fontWeight: "500",
    },
    tableWrapper: {
      overflowX: "auto",
      width: "100%",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "600px",
    },
    th: {
      padding: "10px",
      textAlign: "left",
      backgroundColor: "#8B1E3F",
      color: "#fff",
      border: "1px solid #ddd",
      fontSize: "0.9rem",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "10px",
      border: "1px solid #ddd",
      fontSize: "0.9rem",
      textAlign: "left",
    },
    actionButton: (bg) => ({
      padding: "6px 10px",
      marginRight: "5px",
      backgroundColor: bg,
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.8rem",
      transition: "0.2s",
    }),
    pagination: {
      marginTop: "15px",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      fontSize: "0.9rem",
    },
    pageBtn: {
      padding: "6px 12px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      cursor: "pointer",
      background: "#f9f9f9",
      fontSize: "0.85rem",
      transition: "0.3s",
    },
    loadingText: {
      textAlign: "center",
      padding: "20px",
      color: "#666",
    },
  };

  return (
    <Layout>
      <div style={styles.pageContainer}>
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.leftHeader}>
              <div style={styles.title}>Rto City Master Grid</div>
              <input
                type="text"
                placeholder="Search by City"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={styles.searchInput}
              />
              <button style={styles.searchButton} onClick={handleSearch}>
                Search
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={styles.select}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <button
                style={styles.addButton}
                onClick={() => navigate("/RtoCityMaster")}
              >
                Add New
              </button>
            </div>
          </div>
          {/* Table */}
          <div style={styles.tableWrapper}>
            {loading ? (
              <div style={styles.loadingText}>Loading data...</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Rto City</th>
                    <th style={styles.th}>Main City</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <tr key={index}>
                        <td style={styles.td}>{row.sp_337}</td>
                        <td style={styles.td}>{row.sp_338}</td>

                        <td style={styles.td}>
                          <button
                            style={styles.actionButton("#4CAF50")}
                            onClick={() => handleEdit(row.sp_332)}
                          >
                            Edit
                          </button>
                          <button
                            style={styles.actionButton("#f44336")}
                            onClick={() => handleDelete(row.sp_332)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td style={styles.td} colSpan="6">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          {/* Pagination */}
          <div style={styles.pagination}>
            <button
              style={styles.pageBtn}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              style={styles.pageBtn}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};
export default RtoCityMasterGrid;
